import React, { useState, useEffect } from 'react';
import { ShoppingCart, Truck, Info } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { Link } from 'react-router-dom';

const ShopCanada = () => {
  const [selectedVariety, setSelectedVariety] = useState('all');
  const { dispatch } = useCart();
  const { setCurrency } = useCurrency();

  useEffect(() => {
    setCurrency('CAD');
  }, [setCurrency]);

  const mangoes = [
    {
      id: 'ca-1',
      name: 'Alphonso Box',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1590244843380-09f8fc7a7c4c?auto=format&fit=crop&q=80',
      price: 32.99,
      description: 'The king of mangoes, rich and creamy with a distinctive aroma',
      origin: 'Maharashtra, India',
      season: 'March - June',
      weight: '3kg box'
    },
    {
      id: 'ca-2',
      name: 'Banganapalli Box',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1519096845289-95806ee03a1a?auto=format&fit=crop&q=80',
      price: 35.99,
      description: 'Sweet and aromatic golden mangoes, fiber-free pulp',
      origin: 'Andhra Pradesh, India',
      season: 'March - June',
      weight: '3kg box'
    },
    {
      id: 'ca-3',
      name: 'Kesar Box',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1553279768-865429fa0078?auto=format&fit=crop&q=80',
      price: 35.99,
      description: 'The queen of mangoes, known for its sweet and rich pulp',
      origin: 'Gujarat, India',
      season: 'March - June',
      weight: '3kg box'
    },
    {
      id: 'ca-4',
      name: 'Nuzvidu Rasalu Box',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1601493700631-2b16ec4b4716?auto=format&fit=crop&q=80',
      price: 35.99,
      description: 'Legendary mangoes from Nuzvidu, exceptionally sweet and aromatic',
      origin: 'Andhra Pradesh, India',
      season: 'March - June',
      weight: '3kg box'
    },
    {
      id: 'ca-5',
      name: 'Mixed Premium Box',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1591073113125-e46713c829ed?auto=format&fit=crop&q=80',
      price: 63.99,
      description: 'A curated selection of Alphonso, Banganapalli, and Kesar mangoes',
      origin: 'India',
      season: 'March - June',
      weight: '6kg box'
    }
  ];

  const filteredMangoes = selectedVariety === 'all'
    ? mangoes
    : mangoes.filter(mango => mango.variety === selectedVariety);

  const handleAddToCart = (mango: typeof mangoes[0]) => {
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: mango.id,
        name: mango.name,
        price: mango.price,
        quantity: 1,
        image: mango.image
      }
    });
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Canadian Mango Shop</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Premium Indian mangoes delivered to your doorstep in Canada. 
            Experience the authentic taste of India's finest mangoes.
          </p>
          <div className="mt-6 space-y-4">
            <div className="bg-blue-50 text-blue-700 p-4 rounded-lg inline-block">
              <p>🍁 Free shipping on orders over C$300</p>
            </div>
            <Link 
              to="/shipping-info" 
              className="inline-flex items-center text-yellow-600 hover:text-yellow-700 bg-yellow-50 px-4 py-2 rounded-full"
            >
              <Truck className="h-5 w-5 mr-2" />
              View Shipping Information
              <Info className="h-5 w-5 ml-2" />
            </Link>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredMangoes.map((mango) => (
            <div key={mango.id} className="bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
              <div className="relative">
                <img
                  src={mango.image}
                  alt={mango.name}
                  className="w-full h-64 object-cover"
                />
                <div className="absolute top-4 right-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                  Premium
                </div>
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-2">{mango.name}</h2>
                <p className="text-gray-600 mb-4">{mango.description}</p>
                <div className="mb-4 space-y-1">
                  <p className="text-sm text-gray-500 flex items-center">
                    <span className="font-medium mr-2">Weight:</span> {mango.weight}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center">
                    <span className="font-medium mr-2">Origin:</span> {mango.origin}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center">
                    <span className="font-medium mr-2">Season:</span> {mango.season}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-yellow-500">C${mango.price}</span>
                  <button
                    onClick={() => handleAddToCart(mango)}
                    className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center"
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShopCanada;